import { useEffect, useMemo, useRef, useState } from 'react';
import { classifyType } from '@/lib/values';
import { Popover } from '@/components/common/Popover';
import type { ColumnSchema } from '@/types/api';

/**
 * Searchable column combobox.
 *
 * A plain <select> is untenable here: raqeem.patient_yearly is 595 columns
 * wide. This filters as you type and is fully keyboard driven.
 */

export const TYPE_BADGE: Record<string, string> = {
  number: '#',
  bigint: '#',
  boolean: '01',
  temporal: '◷',
  nested: '{}',
  binary: '0x',
  string: 'ᴀʙᴄ',
};

export interface ColumnPickerProps {
  readonly columns: readonly ColumnSchema[];
  readonly value: string;
  readonly onChange: (column: string) => void;
  readonly ariaLabel: string;
  readonly width?: number;
}

export function ColumnPicker({
  columns,
  value,
  onChange,
  ariaLabel,
  width = 150,
}: ColumnPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(0);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === '') return columns.slice(0, 200);
    return columns
      .filter((column) => column.name.toLowerCase().includes(needle))
      .slice(0, 200);
  }, [columns, search]);

  useEffect(() => setActive(0), [search]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const choose = (name: string | undefined): void => {
    if (!name) return;
    onChange(name);
    setOpen(false);
    setSearch('');
  };

  const selected = columns.find((column) => column.name === value);

  return (
    <div style={{ width }}>
      <button
        ref={anchorRef}
        type="button"
        className="field flex items-center gap-1.5 text-left"
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        {selected ? (
          <span className="eyebrow shrink-0 text-faint" aria-hidden="true">
            {TYPE_BADGE[classifyType(selected.type)] ?? 'ᴀʙᴄ'}
          </span>
        ) : null}
        <span className={`min-w-0 flex-1 truncate font-mono text-[11px] ${value ? '' : 'text-faint'}`}>
          {value || 'column'}
        </span>
        <span className="shrink-0 text-[9px] text-faint" aria-hidden="true">
          ▾
        </span>
      </button>

      {open ? (
        <Popover
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
          width={256}
          role="dialog"
          ariaLabel={`${ariaLabel} options`}
          testId="column-picker-popover"
        >
          <input
            ref={inputRef}
            className="w-full border-b border-line bg-transparent px-2 py-1.5 text-xs outline-none placeholder:text-faint"
            placeholder={`Search ${columns.length} columns`}
            aria-label="Search columns"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setActive((current) => Math.min(matches.length - 1, current + 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setActive((current) => Math.max(0, current - 1));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                choose(matches[active]?.name);
              } else if (event.key === 'Escape') {
                event.preventDefault();
                setOpen(false);
              }
            }}
          />
          <div role="listbox" aria-label="Columns" className="min-h-0 flex-1 overflow-auto py-0.5">
            {matches.map((column, index) => (
              <div
                key={column.name}
                role="option"
                aria-selected={column.name === value}
                className="flex cursor-pointer items-center gap-1.5 px-2 py-1"
                style={{ background: index === active ? 'var(--c-selection)' : 'transparent' }}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(column.name);
                }}
              >
                <span className="eyebrow shrink-0 text-faint" aria-hidden="true">
                  {TYPE_BADGE[classifyType(column.type)] ?? 'ᴀʙᴄ'}
                </span>
                <span className="min-w-0 flex-1 truncate font-mono text-[11px]">{column.name}</span>
                <span className="shrink-0 text-[10px] text-faint">
                  {column.typeText.toLowerCase()}
                </span>
              </div>
            ))}
            {matches.length === 0 ? (
              <p className="px-2 py-3 text-center text-[11px] text-muted">No column matches.</p>
            ) : null}
          </div>
        </Popover>
      ) : null}
    </div>
  );
}
